import {useDispatch, useSelector} from "react-redux";
import {
  listConfigSelector,
  PERIOD_TYPE,
  rxSearch,
  rxShowTransactionEditor,
  rxToggleAllTransactions,
  rxToggleSelectedTransaction,
  selectAllTransactions,
} from "../../../slices/transactionsSlice";
import {selectAllAccounts} from "../../../slices/accountsSlice";
import {TRANSACTION_TYPE_TRANSFER} from "../../../codes";
import {useMemo} from "react";
import {selectAllCategories} from "../../../slices/categoriesSlice";
import {selectAllTags} from "../../../slices/tagsSlice";
import {listToMap} from "../../../util/util";
import {rxShowMileageLogEditor, selectAllMileageLogs} from "../../../slices/mileageLogsSlice";
import {selectAllCars} from "../../../slices/carsSlice";
import {rxShowPortfolioTransactionEditor, selectAllPortfolioTransactions} from "../../../slices/portfolioSlice";
import {selectAllQuotes} from "../../../slices/quotesSlice";

export const CATEGORY_ID_UNKNOWN = "0";
export const CATEGORY_ID_TRANSFER = "1";
export const CATEGORY_ID_INACTIVE = "2";

const filterTransactions = (periodType, periodStart, selectedAccounts, selectedCategories, searchValue, categoriesById, transactions) => {
  return periodType.getTransactions(transactions, periodStart)
    .filter(t => {
      let categoryId = t.category;

      if (t.type === TRANSACTION_TYPE_TRANSFER) {
        categoryId = CATEGORY_ID_TRANSFER;
      } else if (!categoryId) {
        categoryId = CATEGORY_ID_UNKNOWN;
      } else if (!categoriesById[categoryId].active) {
        categoryId = CATEGORY_ID_INACTIVE;
      }

      const accountFilter = selectedAccounts[t.account] || selectedAccounts[t.account] === undefined;
      const categoryFilter = selectedCategories[categoryId] || selectedCategories[categoryId] === undefined;
      const searchFilter = searchValue.length === 0 ||
        ((t.payeeDisplay || t.payee) + (t.note || "")).toLowerCase().match(searchValue.toLowerCase()) ||
        (/^tag:([0-9a-zA-Z]{1,20})$/.test(searchValue) && t.tags && t.tags.map(tag => "tag:" + tag).indexOf(searchValue) >= 0);

      return accountFilter && categoryFilter && searchFilter;
    });
};

const useTransactions = () => {
  const dispatch = useDispatch();

  const {
    periodType: periodTypePosition,
    listKey,
    periodStart,
    selectedTransactions,
    selectedAccounts,
    selectedCategories,
    searchValue
  } = useSelector(listConfigSelector);

  const accountsList = useSelector(selectAllAccounts);
  const transactionCategoriesList = useSelector(selectAllCategories);
  const transactionTagsList = useSelector(selectAllTags);
  const carsList = useSelector(selectAllCars);
  const mileageLogsList = useSelector(selectAllMileageLogs);
  const portfolioTransactionsList = useSelector(selectAllPortfolioTransactions);
  const quotes = useSelector(selectAllQuotes);

  const accounts = useMemo(() => listToMap(accountsList, "id"), [accountsList]);
  const transactionCategories = useMemo(() => listToMap(transactionCategoriesList, "id"), [transactionCategoriesList]);
  const transactionTags = useMemo(() => listToMap(transactionTagsList, "name"), [transactionTagsList]);
  const cars = useMemo(() => listToMap(carsList, "id"), [carsList]);

  const mileageLogs = useMemo(() => listToMap(mileageLogsList.filter(ml => ml.transaction), "transaction"), [mileageLogsList]);
  const portfolioTransactions = useMemo(() => listToMap(portfolioTransactionsList.filter(sp => sp.transaction), "transaction"), [portfolioTransactionsList]);

  const periodType = PERIOD_TYPE[periodTypePosition];

  const allTransactions = useSelector(selectAllTransactions);

  const transactions = filterTransactions(periodType, periodStart, selectedAccounts, selectedCategories, searchValue, transactionCategories, allTransactions);

  const toggleSelectedTransaction = transaction => dispatch(rxToggleSelectedTransaction({transaction}));

  const toggleAllTransactions = () => {
    const val = {};

    if (Object.keys(selectedTransactions).length !== transactions.length) {
      transactions.forEach(t => val[t.id] = true);
    }

    dispatch(rxToggleAllTransactions({selectedTransactions: val}));
  };

  const showTransactionEditor = (mode, transaction) => dispatch(rxShowTransactionEditor({mode, transaction}));
  const showMileageLogEditor = (mode, mileageLog) => dispatch(rxShowMileageLogEditor({mode, mileageLog}));
  const showPortfolioTransactionEditor = (mode, transaction) => dispatch(rxShowPortfolioTransactionEditor({
    mode,
    transaction
  }));

  const searchTag = tag => {
    dispatch(rxSearch({searchValue: "tag:" + tag}));
  };

  return {
    listKey,
    accounts,
    transactionCategories,
    transactionTags,
    filteredTransactions: transactions,
    transactions,
    selectedTransactions,
    selectedAccounts,
    toggleSelectedTransaction,
    toggleAllTransactions,
    searchValue,
    showTransactionEditor,
    showMileageLogEditor,
    showPortfolioTransactionEditor,
    searchTag,
    cars,
    mileageLogs,
    portfolioTransactions,
    quotes
  };
};

export default useTransactions;
